export type WorkType = "talking" | "course";
export type WorkStatus = "done" | "processing" | "failed" | "draft" | "queued";
export type WorkFilter = "all" | WorkType;
export type HumanScope = "public" | "mine";
export type HumanFilter = "all" | HumanScope;
export type TemplateScope = "public" | "mine";
export type TemplateFilter = "all" | TemplateScope;

export interface WorkItem {
  id: string;
  type: WorkType;
  title: string;
  status: WorkStatus;
  date: string;
  duration: string;
  ratio: string;
  progress?: number;
}

export interface AvatarItem {
  id: string;
  name: string;
  scope: HumanScope;
  tone: string;
  color: string;
}

export interface VoiceItem {
  id: string;
  name: string;
  scope: HumanScope;
  /** 描述文案，如「普通话 · 自然讲解」「克隆音色 · 已授权」 */
  meta: string;
  /** 图标底色 */
  color: string;
}

export interface TemplateItem {
  id: string;
  name: string;
  scope: TemplateScope;
  layout: string;
  ratio: string;
  humanColor: string;
}

export const LANGUAGES = [
  { value: "zh", label: "中文" },
  { value: "en", label: "英语" },
  { value: "ja", label: "日语" },
  { value: "de", label: "德语" },
  { value: "fr", label: "法语" },
  { value: "es", label: "西班牙语" },
  { value: "ko", label: "韩语" },
  { value: "ar", label: "阿拉伯语" },
  { value: "ru", label: "俄语" },
  { value: "nl", label: "荷兰语" },
  { value: "it", label: "意大利语" },
  { value: "pl", label: "波兰语" },
  { value: "pt", label: "葡萄牙语" },
];
export const RATIOS = ["16:9", "9:16", "4:3", "21:9"];
export const RESOLUTIONS = ["1080p", "2K", "4K"];
export const BACKGROUNDS = ["演播室", "纯白", "商务蓝", "浅灰", "深色", "柔和渐变", "透明背景"];
export const TRANSPARENT_BACKGROUNDS = ["透明背景"];

export const BG_COLORS: Record<string, string> = {
  演播室: "linear-gradient(135deg, #2a3a4d 0%, #1a2533 100%)",
  纯白: "#ffffff",
  商务蓝: "linear-gradient(135deg, #1e5f9e 0%, #143d66 100%)",
  浅灰: "#eef1f4",
  深色: "#111827",
  柔和渐变: "linear-gradient(135deg, #ffe8d6 0%, #f3e8ff 100%)",
  透明背景: "transparent",
};

export const isTransparentBg = (name: string) => TRANSPARENT_BACKGROUNDS.includes(name);

export const SAMPLE_WORKS: WorkItem[] = [
  { id: "w1", type: "talking", title: "AI 产品经理入门课", status: "draft", date: "2026-09-10", duration: "02:34", ratio: "16:9" },
  { id: "w2", type: "talking", title: "九月新品口播（副本）", status: "done", date: "2026-09-08", duration: "01:12", ratio: "9:16" },
  { id: "w3", type: "talking", title: "新员工合规培训（副本）", status: "draft", date: "2026-09-11", duration: "00:48", ratio: "16:9" },
  { id: "w4", type: "course", title: "AI 产品经理入门课", status: "done", date: "2026-09-05", duration: "03:26", ratio: "16:9" },
  { id: "w5", type: "course", title: "新员工合规培训（副本）", status: "failed", date: "2026-09-03", duration: "05:10", ratio: "16:9" },
  { id: "w6", type: "course", title: "产品功能讲解课", status: "processing", date: "2026-08-29", duration: "04:02", ratio: "4:3", progress: 68 },
  { id: "w7", type: "talking", title: "企业英语口语", status: "done", date: "2026-09-01", duration: "01:45", ratio: "16:9" },
  { id: "w8", type: "talking", title: "九月新品口播", status: "done", date: "2026-08-30", duration: "00:52", ratio: "9:16" },
  { id: "w9", type: "course", title: "AI 产品经理入门课", status: "draft", date: "2026-08-28", duration: "03:15", ratio: "16:9" },
];

export const SAMPLE_AVATARS: AvatarItem[] = [
  { id: "a1", name: "思萌", scope: "public", tone: "可用于口播视频与视频课", color: "#3b5b7a" },
  { id: "a2", name: "林溪", scope: "public", tone: "可用于口播视频与视频课", color: "#b85c5c" },
  { id: "a3", name: "江白", scope: "public", tone: "可用于口播视频与视频课", color: "#4a8a6a" },
  { id: "a4", name: "企业讲师小林", scope: "mine", tone: "可用于口播视频与视频课", color: "#b85c5c" },
];

export const SAMPLE_VOICES: VoiceItem[] = [
  { id: "v1", name: "温柔女声", scope: "public", meta: "普通话 · 自然讲解", color: "#3267fe" },
  { id: "v2", name: "磁性男声", scope: "public", meta: "普通话 · 沉稳播报", color: "#b85c5c" },
  { id: "v3", name: "知性女声", scope: "public", meta: "普通话 · 亲和清晰", color: "#7a5ab8" },
  { id: "v4", name: "企业讲师周然", scope: "mine", meta: "克隆音色 · 已授权", color: "#0e7a5f" },
];

export const SAMPLE_TEMPLATES: TemplateItem[] = [
  { id: "t1", name: "课件＋角落讲师", scope: "public", layout: "overlay", ratio: "16:9", humanColor: "#53677e" },
  { id: "t2", name: "左右双栏讲解", scope: "public", layout: "split", ratio: "16:9", humanColor: "#53677e" },
  { id: "t3", name: "纯课件讲解", scope: "mine", layout: "full", ratio: "16:9", humanColor: "#53677e" },
];

export const COURSE_PAGES = [
  { id: "p1", title: "封面 · 课程介绍", script: "欢迎来到 AI 产品经理入门课，我是你的讲师。" },
  { id: "p2", title: "什么是 AI 产品经理", script: "AI 产品经理负责定义智能产品的边界与体验。" },
  { id: "p3", title: "核心能力模型", script: "技术理解、用户洞察、数据驱动决策三者缺一不可。" },
  { id: "p4", title: "需求分析方法", script: "从真实场景出发，拆解用户目标与约束。" },
  { id: "p5", title: "模型选型要点", script: "根据延迟、成本与准确性权衡基础模型。" },
  { id: "p6", title: "课程总结", script: "把今天的方法带回你的下一个项目里。" },
];

export function statusText(status: WorkStatus) {
  switch (status) {
    case "done": return "已完成";
    case "processing": return "创作中";
    case "queued": return "排队中";
    case "failed": return "失败";
    default: return "草稿";
  }
}

export function typeText(type: WorkType) {
  return type === "talking" ? "口播视频" : "视频课";
}
